import {useCallback, useMemo, useState} from 'react';
import {PageDataStore, TablePaginationConfig} from 'utils/hooks/useNewPageData';
import {getTableGetParams} from 'utils/hooks/useNewPageData/utils';
import {FilterConfig, SortConfig} from 'components/NewTable/types';
import {ParamsType} from 'utils';
import {useMutation} from '@tanstack/react-query';
import {formatDataForTables} from '@ui/NewTable/formatDataForTable';

type useTableDataProps = {
    getData: (params: ParamsType) => Promise<any>;
    needFormatting?: boolean;
    additionalParams?: Record<string, any>;
    customFormatteData?: (data: any) => any;
};

export const useTableData = <T extends {}>({
    getData,
    needFormatting,
    additionalParams = {},
    customFormatteData,
}: useTableDataProps) => {
    const [store, setStore] = useState<PageDataStore<T>>();

    const mutateGetTableData = useMutation({
        mutationKey: ['getTableData'],
        mutationFn: (params: ParamsType) => getData(params),
        onSuccess: (data) => {
            if (needFormatting) {
                const formattedData = customFormatteData
                    ? customFormatteData(data)
                    : formatDataForTables<T>(data);
                setStore({
                    data: formattedData,
                    total: formattedData?.page?.totalElements,
                    page: formattedData?.page?.number,
                    pageSize: formattedData?.page?.size,
                });
                return;
            }
            setStore({
                data: data,
                total: data?.page?.totalElements,
                page: data?.page?.number,
                pageSize: data?.page?.size,
            });
        },
    });

    const update = useCallback(
        (pagination?: TablePaginationConfig, sort?: SortConfig, filters?: FilterConfig) => {
            mutateGetTableData.mutate({
                ...getTableGetParams(pagination, sort),
                ...filters,
                ...additionalParams,
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [additionalParams],
    );

    return useMemo(() => [store, update] as const, [store, update]);
};
